<!-- Section 9 -->
<section id="section-10" class="section-divider-ipg">
  <h3 class="heading-2-ipg">Section Nine</h3>
  <h3 class="heading-3-ipg">Pronouns (<span class="italic-w-ipg">nāma</span>)</h3>

  <p class="paragraph-1-ipg">In Pāli the pronouns present their individual declensions. These are different from the declensions of the nouns. But all pronouns — personal, demonstrative, relative and interrogative — follow a similar pattern. </p>

  <p class="paragraph-1-ipg">
    The demonstrative pronouns are <span class="italic-w-ipg">so/sā/taṃ</span>, <span class="italic-w-ipg">eso/esā/etaṃ</span> and <span class="italic-w-ipg">ena/naṃ</span>; all follow the same declension. <br>
    <span class="italic-w-ipg">ayaṃ/idaṃ</span> refer to a person or thing in close vicinity while <span class="italic-w-ipg">asu/aduṃ</span> point to a person or thing farther away; they follow the declensions in the same manner as those listed below.</p>
    
  <p>Pāli further uses possessive pronouns: <span class="italic-w-ipg">mādīya; tādīya</span>, but they are of rare occurrence and are substituted by the genitive of the personal pronoun for the 1st and 2nd person; the 3rd person uses the genitive of the demonstrative pronoun.
  </p>

  <p class="paragraph-1-ipg">The indefinite pronouns formed with particles <span class="italic-w-ipg">ci/cid</span> and <span class="italic-w-ipg">api/pi</span> are often used and are listed below.</p>

  <h4 id="section-10-1" class="subheading-1-ipg">1.
    Personal Pronoun: 1st person:<br> <span class="italic-w-ipg">ahaṃ</span> – I / <span class="italic-w-ipg">mayaṃ, amhe</span> – we</h4>

  <!-- table 1, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">      
      <tr>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg">Singular</th>
        <th class="list-chart-4-ipg">Plural</th>   
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ahaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">mayaṃ, amhe</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">maṃ, mamaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amhe, amhākaṃ, no</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">mayā, me</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amhehi, no</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">mama, mayhaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amhaṃ, amhākaṃ, no</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">mayā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amhehi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">mama, mayhaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amhaṃ, amhākaṃ, no</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">mayi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amhesu</span></td>
      </tr>
    </table>
  </div>

  <h4 id="section-10-2" class="subheading-1-ipg">2.	2nd person:<br> <span class="italic-w-ipg">tvaṃ</span>, <span class="italic-w-ipg">tuvaṃ</span>	– you / <span class="italic-w-ipg">tumhe</span> – you</h4>

  <!-- table 1, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">      
      <tr>
        <th class="list-chart-4-ipg"></th>
        <th class="list-chart-4-ipg">Singular</th>
        <th class="list-chart-4-ipg">Plural</th>  
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tvaṃ, tuvaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tumhe</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">taṃ, tavaṃ, tuvaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tumhe, tumhākaṃ, vo</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tvayā, tayā, te</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tumhehi, vo</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">taya, tuyhaṃ, te</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tumhaṃ, tumhākaṃ, vo</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tvayā, tayā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tumhehi, vo</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tava, tuyhaṃ, te</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tumhaṃ, tumhākaṃ, vo</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tvayi, tayi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tumhesu</span></td>
      </tr>
    </table>
  </div>

  <p class="paragraph-1-ipg">   
    The following tables show the declensions for the 3rd person in the respective order of demonstrative pronoun, relative pronoun and interrogative pronoun, in singular and plural.
  </p>



  <h4 id="section-10-3" class="subheading-1-ipg">3.	Masculine, 3rd person, singular:<br> <span class="italic-w-ipg">so</span>	– he</h4>

  <!-- table 1, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">      
      <tr>
        <th class="list-chart-4-ipg">Case</th>
        <th class="list-chart-4-ipg">Demonstrative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Relative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Interrogative <br>Pronoun</th>
      </tr>  	
      <tr>
        <th class="list-chart-4-ipg"></th>
        <td class="list-chart-3-ipg">he, that</td>
        <td class="list-chart-3-ipg">he, who</td>
        <td class="list-chart-3-ipg">who?</td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">so</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yo</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ko</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">taṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tena</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yena</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kena</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tassa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yassa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kassa, kissa</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tamhā, tasmā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yamhā, yasmā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kasmā, kismā</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tassa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yassa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kassa, kissa</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tamhi, tasmiṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yamhi, yasmiṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kamhi, kasmiṃ, kimhi, kismiṃ</span></td>
      </tr>
    </table>
  </div>

  <h4 id="section-10-4" class="subheading-1-ipg">4.	Neuter, 3rd person, singular:<br> <span class="italic-w-ipg">yaṃ</span>	– it, that</h4>

  <!-- table 1, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">      
      <tr>
        <th class="list-chart-4-ipg">Case</th>
        <th class="list-chart-4-ipg">Demonstrative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Relative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Interrogative <br>Pronoun</th>
      </tr>
      <tr>
        <th class="list-chart-4-ipg"></th>
        <td class="list-chart-3-ipg">it, that</td>
        <td class="list-chart-3-ipg">that, which</td>
        <td class="list-chart-3-ipg">which?</td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">taṃ</span></td>      
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kiṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">taṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kiṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tena</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yena</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kena</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tassa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yassa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kassa, kissa</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tamhā, tasmā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yamhā, yasmā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kasmā, kismā</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tassa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yassa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kassa, kissa</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tamhi, tasmiṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yamhi, yasmiṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kamhi, kasmiṃ, kimhi, kismiṃ</span></td>
      </tr>
    </table>
  </div>


  <h4 id="section-10-5" class="subheading-1-ipg">5.	Feminine, 3rd person, singular:<br> <span class="italic-w-ipg">yā</span> – she, who</h4>

  <!-- table 1, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">      
      <tr>
        <th class="list-chart-4-ipg">Case</th>
        <th class="list-chart-4-ipg">Demonstrative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Relative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Interrogative <br>Pronoun</th>
      </tr>
      <tr>
        <th class="list-chart-4-ipg"></th>
        <td class="list-chart-3-ipg">she, that</td>
        <td class="list-chart-3-ipg">she who</td>
        <td class="list-chart-3-ipg">who?</td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">sā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kā</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">taṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kāya</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tassā, tāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yassā, yāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kassā, kāya</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kāya</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tassā, tāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yassā, yāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kassā, kāya</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tassaṃ, tāyaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yassaṃ, yāyaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kassaṃ, kāyaṃ</span></td>
      </tr>
    </table>
  </div>


  <h4 id="section-10-6" class="subheading-1-ipg">6.	Masculine, 3rd person, plural:<br> <span class="italic-w-ipg">ye</span> – they</h4>

  <!-- table 1, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">      
      <tr>
        <th class="list-chart-4-ipg">Case</th>
        <th class="list-chart-4-ipg">Demonstrative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Relative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Interrogative <br>Pronoun</th>
      </tr>
      <tr>
        <th class="list-chart-4-ipg"></th>
        <td class="list-chart-3-ipg">they, those</td>
        <td class="list-chart-3-ipg">they, who</td>
        <td class="list-chart-3-ipg">who?</td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">te</span></td>	
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ye</span></td>	
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ke</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">te</span></td>	
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ye</span></td>	
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ke</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tehi</span></td>	
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yehi</span></td>	
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kehi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tesaṃ, tesānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yesaṃ, yesānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kesaṃ, kesānaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tehi</span></td>	
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yehi</span></td>	
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kehi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tesaṃ, tesānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yesaṃ, yesānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kesaṃ, kesānaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tesu</span></td>	
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yesu</span></td>	
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kesu</span></td>
      </tr>
    </table>
  </div>


  <h4 id="section-10-7" class="subheading-1-ipg">7.	Neuter, 3rd person, plural:<br> <span class="italic-w-ipg">yāni, ye</span> – those</h4>

  <!-- table 1, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">      
      <tr>
        <th class="list-chart-4-ipg">Case</th>
        <th class="list-chart-4-ipg">Demonstrative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Relative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Interrogative <br>Pronoun</th>
      </tr>
      <tr>
        <th class="list-chart-4-ipg"></th>
        <td class="list-chart-3-ipg">they, those</td>
        <td class="list-chart-3-ipg">they, who</td>
        <td class="list-chart-3-ipg">who?</td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tāni, te</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yāni, ye</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kāni, ke</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tāni, te</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yāni, ye</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kāni, ke</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tehi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yehi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kehi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tesaṃ, tesānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yesaṃ, yesānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kesaṃ, kesānaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tehi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yehi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kehi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tesaṃ, tesānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yesaṃ, yesānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kesaṃ, kesānaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tesu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yesu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kesu</span></td>
      </tr>
    </table>
  </div>


  <h4 id="section-10-8" class="subheading-1-ipg">8.	Feminine, 3rd person, plural:<br> <span class="italic-w-ipg">tā, tāyo</span> – they, those</h4>

  <!-- table 1, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">      
      <tr>
        <th class="list-chart-4-ipg">Case</th>
        <th class="list-chart-4-ipg">Demonstrative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Relative <br>Pronoun</th>
        <th class="list-chart-4-ipg">Interrogative <br>Pronoun</th>
      </tr>
      <tr>
        <th class="list-chart-4-ipg"></th>
        <td class="list-chart-3-ipg">those</td>
        <td class="list-chart-3-ipg">those</td>
        <td class="list-chart-3-ipg">who?</td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tā, tāyo</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yā, yāyo</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kā, kāyo</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tā, tāyo</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yā, yāyo </span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kā, kāyo</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tāhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yāhi </span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kāhi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tāsaṃ, tāsānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yāsaṃ, yāsānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kāsaṃ, kāsānaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tāhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yāhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kāhi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tāsaṃ, tāsānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yāsaṃ, yāsānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kāsaṃ, kāsānaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">tāsu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">yāsu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">kāsu</span></td>
      </tr>
    </table>
  </div>


  <h4 id="section-10-9" class="subheading-1-ipg">9.	Declension of:<br> <span class="italic-w-ipg">ayaṃ, idaṃ</span> – this</h4>
  <!-- table 1, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">      
      <tr>
        <th class="list-chart-4-ipg">Case</th>
        <th class="list-chart-4-ipg">Masculine</th>
        <th class="list-chart-4-ipg">Feminine</th>
        <th class="list-chart-4-ipg">Neuter</th>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Singular</th>
        <td class="list-chart-3-ipg">this / he</td> 
        <td class="list-chart-3-ipg">this / she</td>
        <td class="list-chart-3-ipg">this / it</td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ayaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ayaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">idaṃ / imaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">idaṃ / imaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">iminā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imāya </span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">iminā / aminā</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imassa / assa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imissā / imissāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imassa / assa</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imasmā/ imamhā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imasmā / imamhā</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imassa / assa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imissā / imissāya / imāya</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imassa / assa</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imasmiṃ / imamhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imissaṃ / imissā / imāyaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imasmiṃ / imamhi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Plural</th>
        <td class="list-chart-3-ipg"></td>
        <td class="list-chart-3-ipg"></td>
        <td class="list-chart-3-ipg"></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ime</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imā / imāyo</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imāni</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">ime</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imā / imāyo</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imāni</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imehi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imāhi </span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imehi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imesaṃ / imesānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imāsaṃ / imāsānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imesaṃ / imesānaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imehi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imāhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imehi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imesaṃ / imesānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imāsaṃ / imāsānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imesaṃ / imesānaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imesu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imāsu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">imesu</span></td>
      </tr>
    </table>
  </div>

  <h4 id="section-10-10" class="subheading-1-ipg">10.	Declension of:<br> <span class="italic-w-ipg">asu, amu</span> – that</h4>
  <!-- table 1, left header and top-row header -->
  <div class="responsive-table-ipg table-regular-ipg width-table-1">
    <table class="vowel-list-2 table-regular-ipg width-table-1">      
      <tr>
        <th class="list-chart-4-ipg">Case</th>
        <th class="list-chart-4-ipg">Masculine</th>
        <th class="list-chart-4-ipg">Feminine</th>
        <th class="list-chart-4-ipg">Neuter</th>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Singular</th>
        <td class="list-chart-3-ipg"></td>
        <td class="list-chart-3-ipg"></td>
        <td class="list-chart-3-ipg"></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">asu, amu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">asu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">aduṃ / amuṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amuṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amuṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">aduṃ / amuṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amunā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amuyā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amunā</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amussa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amussā / amuyā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amussa</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amusmā / amumhā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amuyā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amusmā / amumhā</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amussa</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amussā / amuyā</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amussa</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-shart-3-ipg"><span class="italic-w-ipg">amusmiṃ / amumhi</span></td>
        <td class="list-shart-3-ipg"><span class="italic-w-ipg">amussaṃ / amuyaṃ</span></td>
        <td class="list-shart-3-ipg"><span class="italic-w-ipg">amusmiṃ / amumhi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Plural</th>
        <td class="list-chart-3-ipg"></td>
        <td class="list-chart-3-ipg"></td>
        <td class="list-chart-3-ipg"></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Nominative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amū</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amū / amūyo</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amū / amūni</span></td>
      </tr> 
      <tr>
        <th class="list-chart-4-ipg">Accusative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amū</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amū / amūyo</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amū / amūni</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Instrumental</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūhi/amūbhi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Dative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūsaṃ / amūsānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūsaṃ / amūsānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūsaṃ / amūsānaṃ</span></td>
      </tr> 
      <tr>
        <th class="list-chart-4-ipg">Ablative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūhi</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūhi / amūbhi</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Genitive</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūsaṃ / amūsānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūsaṃ / amūsānaṃ</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūsaṃ / amūsānaṃ</span></td>
      </tr>
      <tr>
        <th class="list-chart-4-ipg">Locative</th>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūsu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūsu</span></td>
        <td class="list-chart-3-ipg"><span class="italic-w-ipg">amūsu</span></td>
      </tr>
    </table>
  </div>

  <h4 id="section-10-11" class="subheading-1-ipg">11. The Indefinite Particle <span class="italic-w-ipg">ci</span></h4>

  <p class="paragraph-1-ipg">
    The indefinite particles — <span class="italic-w-ipg">ci/cid</span> (-d- inserted before vocals) or <span class="italic-w-ipg">api/pi</span> — are appended to and declined as the case forms of the interrogative pronoun, expressing ideas such as 'anyone, whichever, whoever'.<br>
    Examples are:
  </p>

  <dl>
    <dt>Masc.</dt>
    <dd><span class="italic-w-ipg">koci puriso</span> – some man; </dd>
    <dd><span class="italic-w-ipg">kenaci purisena</span> – by some man;</dd>
    <dd><span class="italic-w-ipg">kassaci purissa</span> – of some man; etc.</dd>
    <dt>Neut.</dt>
    <dd><span class="italic-w-ipg">kiñci phalaṃ</span> – some fruit; </dd>
    <dd><span class="italic-w-ipg">kenaci phalena</span> – by some fruit;</dd>
    <dd><span class="italic-w-ipg">kassaci phalassa</span> – of some fruit; etc.</dd>
    <dt>Fem.</dt>
    <dd><span class="italic-w-ipg">kāci itthi</span> – some woman; </dd>
    <dd><span class="italic-w-ipg">kāyaci itthiyā</span> – by some woman;</dd>
    <dd><span class="italic-w-ipg">kassāci itthiyā</span> – of some woman; etc</dd>
  </dl>

</section>

  
<!-- No footnotes for section 9 -->

<style>

  
</style>